import { Component, EventEmitter, inject, OnDestroy, OnInit, Output } from '@angular/core'
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms'
import { Store } from '@ngrx/store'
import { selectCtc } from '../../../../store/employee-salary/employee-salary.selectors'
import { filter, Subscription, take, tap } from 'rxjs'
import { CommonModule, NgIf } from '@angular/common'
import { addCtc } from '../../../../store/employee-salary/employee-salary.actions'
import { CTC, CTCBasis } from '../../../../store/employee-salary/employee-salary.state'
import { User } from '../../../../store/user/user.state'
import { selectSelectedUser } from '../../../../store/user/user.reducer'
import { IndianNumberInputFormatDirective } from '../../../../directives/indian-number-input-format.directive'
import dayjs from 'dayjs'
import { selectAllCountries } from '../../../../store/country/country.selectors'
import { CapitalizePipe } from '../../../../pipes/capitalize.pipe'
import { loadCountries } from '../../../../store/country/country.actions'

@Component({
  selector: 'app-ctc',
  imports: [
    NgIf,
    FormsModule,
    CommonModule,
    ReactiveFormsModule,
    IndianNumberInputFormatDirective,
    CapitalizePipe
  ],
  templateUrl: './ctc.component.html',
  styleUrl: './ctc.component.scss'
})
export class CtcComponent implements OnInit, OnDestroy {
  private store = inject(Store)
  private fb = inject(FormBuilder)
  CTCBasis = CTCBasis
  CTCBasisValues = Object.values(CTCBasis)
  form!: FormGroup
  @Output() formValuesChanged = new EventEmitter<CTC>()
  private subscription!: Subscription
  private joiningDate!: string | Date
  countries$ = this.store.select(selectAllCountries)

  ngOnInit(): void {
    this.store.dispatch(loadCountries())
    this.initializeForm()

    // Get the joining date
    // Latest Selected user was already dispatched in the wrapper component
    this.store
      .select(selectSelectedUser)
      .pipe(
        filter((user): user is User => !!user),
        take(1),
        tap(user => {
          if (user.joiningDate) {
            this.joiningDate = dayjs(user.joiningDate).format('YYYY-MM-DD')
          } else {
            this.joiningDate = dayjs().format('YYYY-MM-DD')
          }
        })
      )
      .subscribe()

    // Get the CTC value
    this.store
      .select(selectCtc)
      .pipe(take(1))
      .subscribe(ctc => {
        if (ctc) this.form.patchValue(ctc)
      })

    // Emit live ctc value for Parent Component
    this.subscription =
      this.form.valueChanges.subscribe(value => {
        this.formValuesChanged.emit(value)
      }) ?? new Subscription()
  }

  ngOnDestroy() {
    this.form.patchValue({
      effectiveFrom: this.joiningDate
    })
    this.form.patchValue({
      payoutFrom: dayjs(this.joiningDate).format('YYYY-MM')
    })
    this.store.dispatch(addCtc({ ctc: this.form.value }))
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
    localStorage.setItem('currentCtc', this.form.get('ctc')?.value.toString())
  }

  initializeForm() {
    this.form = this.fb.group({
      basis: [CTCBasis.ANNUALLY, Validators.required],
      currency: ['INR', Validators.required],
      ctc: [0, Validators.min(1)],
      effectiveFrom: ['', Validators.required],
      payoutFrom: ['', Validators.required]
    })
  }

  getErrorMessage(controlName: string): string | null {
    const control = this.form.get(controlName)
    if (control && control.touched && control.invalid) {
      if (control.hasError('required')) {
        return 'This field is required'
      }
    }
    return null // No error
  }
}
